<script lang="ts">
	/**
	 * The app's one frame: `<main>`, then the mini-player and the tab bar, both
	 * mounted HERE and only here, so they persist across navigation and the
	 * route transitions can hold them still while the page changes under them.
	 *
	 * THE NAV LANDMARK IS OUTSIDE `<main>`. The tab bar's `<nav>` comes AFTER
	 * `<main>` in the document, so it never stands between a keyboard user and
	 * the page, and a screen-reader user reaches it by landmark. The skip link
	 * targets `#main`, where each page starts with its app bar (back control
	 * and actions). The reader adds its own in-page skip link
	 * (`read/[surah]/+page.svelte`) that targets the mushaf itself, past the
	 * toolbar and the surah heading.
	 */
	import '../app.css';
	import { onMount } from 'svelte';
	import { startInstallWatch } from '$lib/pwa/install';
	import { registerServiceWorker } from '$lib/pwa/register';
	import { requestPersistence } from '$lib/storage/persist';
	import MiniPlayer from '$lib/ui/audio/MiniPlayer.svelte';
	import { installNavigation } from '$lib/ui/nav/nav.svelte';
	import { skipToTarget } from '$lib/ui/nav/skip';
	import TabBar from '$lib/ui/nav/TabBar.svelte';
	import InstallPrompt from '$lib/ui/pwa/InstallPrompt.svelte';
	import UpdateToast from '$lib/ui/pwa/UpdateToast.svelte';

	let { children } = $props();

	// As early as the app runs (SPA, ssr = false): `beforeinstallprompt` can fire
	// before anything mounts, and a missed one is not sent again.
	startInstallWatch();

	// The history mirror, the pending tint and the route transitions. Kit's
	// navigation hooks must be registered during component initialisation.
	installNavigation();

	onMount(() => {
		// D8: the device is the only copy, so ask the browser to keep it. Silent.
		void requestPersistence();
		// Offline use and installability both need the worker registered.
		registerServiceWorker();
		// iOS applies `:active`, which the press tint is built on, only when a
		// `touchstart` listener exists. Install one deliberately rather than
		// depend on Kit's preload listener happening to be there.
		const noop = () => {};
		document.addEventListener('touchstart', noop, { passive: true });
		return () => document.removeEventListener('touchstart', noop);
	});
</script>

<!-- `skipToTarget`: focus `#main` without pushing a `#main` history entry,
     which the app bar's back control (the next tab stop) would then spend a
     press undoing. -->
<a class="skip-link" href="#main" onclick={skipToTarget}>تخطَّ إلى المحتوى</a>

<!-- Both spacers (the mini-player's and the tab bar's) are in flow inside
     `.shell`, after `<main>`, so the end of every page clears them. -->
<div class="shell">
	<main id="main" tabindex="-1">
		{@render children?.()}
	</main>
	<!-- Recitation (reader extras): shown only while something is queued. -->
	<MiniPlayer />
	<TabBar />
</div>

<!-- Shown only when the app is opened in a browser and can be installed. -->
<InstallPrompt />
<UpdateToast />

<style>
	/* The safe-area inset is subtracted because `body` pads it: short pages
	   (qibla, about, empty bookmarks) then fit the screen exactly instead of
	   scrolling by the inset. */
	.shell {
		display: flex;
		flex-direction: column;
		min-block-size: calc(100dvb - env(safe-area-inset-bottom, 0px));
	}

	/* No top padding: each page's header owns the top spacing (the sticky app
	   bar starts at y = 0), so every title lands on the same baseline. */
	main {
		flex: 1;
		inline-size: min(100%, var(--measure));
		margin-inline: auto;
		padding-inline: var(--space-4);
		padding-block: 0 var(--space-6);
	}

	main:focus {
		outline: none;
	}

	.skip-link {
		position: absolute;
		inset-block-start: var(--space-2);
		inset-inline-start: var(--space-2);
		z-index: 10;
		padding-block: var(--space-2);
		padding-inline: var(--space-4);
		border-radius: var(--radius-md);
		background-color: var(--accent);
		color: var(--accent-fg);
		transform: translateY(-200%);
	}

	.skip-link:focus {
		transform: none;
	}
</style>
